// Pills, chips and tags — see Badge.module.css for what each shape means.

import type { ReactNode } from "react";
import styles from "./Badge.module.css";
import type { BadgeColor } from "../format/labels.js";

/**
 * The design's palette, named by meaning. `labels.ts` decides which tone a
 * given enum value earns; this decides what the tone looks like. Keeping those
 * two apart is what lets "ready to invoice" and "contracting" share a colour
 * deliberately rather than by coincidence.
 */
export type Tone = "neutral" | "info" | "brand" | "warn" | "good" | "bad" | "slate" | "teal";

/**
 * `labels.ts` still speaks the vocabulary the old badge library used. Rather
 * than rewrite every caller in one go, translate at the boundary — the label
 * functions carry real editorial decisions about which states group together,
 * and those survive the restyle unchanged.
 */
const TONE_BY_BADGE_COLOR: Record<BadgeColor, Tone> = {
  subtle: "neutral",
  informative: "info",
  brand: "brand",
  important: "brand",
  warning: "warn",
  severe: "warn",
  success: "good",
  danger: "bad",
};

export function toneFor(color: BadgeColor): Tone {
  return TONE_BY_BADGE_COLOR[color];
}

type Shape = "pill" | "chip" | "tag";

export interface BadgeProps {
  children: ReactNode;
  tone: Tone;
  shape?: Shape;
  className?: string;
}

export function Badge({ children, tone, shape = "pill", className }: BadgeProps): ReactNode {
  return (
    <span
      className={[styles.base, styles[shape], styles[tone], className].filter(Boolean).join(" ")}
    >
      {children}
    </span>
  );
}

export interface InteractiveBadgeProps extends BadgeProps {
  onClick: () => void;
  /** Says what the click will DO, not what the badge says. Required: an
   *  icon-only affordance with no accessible name is unusable by keyboard. */
  label: string;
  disabled?: boolean;
}

/**
 * A badge that advances something when clicked. A `<button>` rather than a
 * `<span onClick>` so it is reachable by keyboard and announces itself as a
 * control — the design draws it as a chip, but it behaves as what it is.
 */
export function InteractiveBadge({
  children,
  tone,
  shape = "chip",
  className,
  onClick,
  label,
  disabled = false,
}: InteractiveBadgeProps): ReactNode {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={[styles.base, styles[shape], styles[tone], styles.interactive, className]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </button>
  );
}
